import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import "../../../../i18n";
import { MethodChip, Notice } from "../../../../design/components";
import type { MethodId } from "../../../../design/methods";
import { ApiError } from "../../../../api/client";
import type { ConnectionMethod } from "../../../../api/types";
import { patchEntity, saveConnection, useConnection } from "../onboardApi";
import { MASK, METHOD_DEFAULTS, MethodForm, SECRET_FIELDS } from "./MethodForm";
import { useOnboardDraft } from "../useOnboardDraft";
import styles from "../onboard.module.css";

export { MASK } from "./MethodForm";

const CHOICES: Array<{ method: ConnectionMethod; id: MethodId; whatKey: string; whatDefault: string }> = [
  {
    method: "direct_api",
    id: 1,
    whatKey: "onboard.connection.choice.directApi",
    whatDefault: "The company gives the hub an API user, and the hub calls its ERP over the internet.",
  },
  {
    method: "agent",
    id: 2,
    whatKey: "onboard.connection.choice.agent",
    whatDefault: "The company runs a small agent inside its own network that calls the hub outward.",
  },
  {
    method: "file",
    id: 3,
    whatKey: "onboard.connection.choice.file",
    whatDefault: "The company writes invoice files into a folder the hub collects on a schedule.",
  },
  {
    method: "push",
    id: 4,
    whatKey: "onboard.connection.choice.push",
    whatDefault: "The ERP sends invoices to the hub in batches. The hub does not connect to the ERP.",
  },
];

export function StepConnection({
  entityId,
  code,
  onSaved,
}: {
  entityId: string;
  code: string;
  onSaved(method: ConnectionMethod): void;
}) {
  const { t } = useTranslation();
  const draft = useOnboardDraft((state) => state.connection);
  const setMethod = useOnboardDraft((state) => state.setMethod);
  const setConfig = useOnboardDraft((state) => state.setConfig);
  const stored = useConnection(entityId);
  const queryClient = useQueryClient();
  const silence = useOnboardDraft((state) => state.identity.silenceThresholdMinutes);
  const families = useOnboardDraft((state) => state.identity.enabledDocTypes);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const seeded = useRef(false);

  // Server settings seed the form once; secrets stay masked until Replace.
  useEffect(() => {
    const config = stored.data?.config;
    if (!config || seeded.current) return;
    seeded.current = true;
    const seed: Record<string, string> = {};
    for (const [key, value] of Object.entries(config)) seed[key] = value == null ? "" : String(value);
    setConfig(seed);
  }, [stored.data, setConfig]);

  const method = draft.method ?? stored.data?.method ?? null;
  const value = (key: string, fallback = "") => draft.config[key] ?? fallback;

  function chooseMethod(next: ConnectionMethod): void {
    setMethod(next);
    const seed = Object.fromEntries(
      Object.entries(METHOD_DEFAULTS[next]).filter(([key]) => draft.config[key] === undefined),
    );
    if (Object.keys(seed).length > 0) setConfig(seed);
  }

  async function submit(): Promise<void> {
    if (!method) return;
    setSaving(true);
    setError("");
    try {
      // A push company keeps no ERP settings: whatever another method left in the draft is not sent.
      const config: Record<string, string> =
        method === "push"
          ? {}
          : Object.fromEntries(
              Object.entries(draft.config).filter(([key, item]) => item !== "" && !(SECRET_FIELDS.includes(key) && item === MASK)),
            );
      // Derived read-only in the form, but the saved config has to name it too.
      if (method === "file") config.drop_folder = `/drop/${code}/in`;
      await saveConnection(entityId, method, config);
      await patchEntity(entityId, { connectionMethod: method, silenceThresholdMinutes: silence });
      // The wizard reads its progress off the server, so the card has to be re-read
      // before it decides which step comes next.
      await queryClient.invalidateQueries({ queryKey: ["entities", entityId, "connection"] });
      await queryClient.invalidateQueries({ queryKey: ["entities", entityId] });
      onSaved(method);
    } catch (exc) {
      setError(
        exc instanceof ApiError
          ? exc.message
          : t("onboard.connection.saveError", "The connection could not be saved."),
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className={styles.panel}>
      <h2>{t("onboard.connection.title", "Connection")}</h2>
      <p className={styles.caption}>
        {t("onboard.connection.caption", "How the hub and this company's ERP reach each other.")}
      </p>

      <div
        className={styles.methods}
        role="radiogroup"
        aria-label={t("onboard.connection.methodGroupLabel", "Connection method")}
      >
        {CHOICES.map((choice) => (
          <div
            key={choice.method}
            className={styles.methodCard}
            role="radio"
            tabIndex={0}
            aria-checked={method === choice.method}
            onClick={() => chooseMethod(choice.method)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                chooseMethod(choice.method);
                e.preventDefault();
              }
            }}
          >
            <MethodChip method={choice.id} full />
            <span className={styles.what}>{t(choice.whatKey, choice.whatDefault)}</span>
          </div>
        ))}
      </div>

      {method ? (
        <MethodForm method={method} prefix="ob" code={code} value={value} setConfig={setConfig} disabled={false} families={families} />
      ) : null}

      {error ? <Notice kind="fail">{error}</Notice> : null}

      <div className={styles.foot}>
        <button
          type="button"
          className="btn btn-primary"
          disabled={!method || saving}
          onClick={() => void submit()}
        >
          {t("onboard.connection.continue", "Continue")}
        </button>
      </div>
    </section>
  );
}
